import { Link, useLocation } from 'react-router-dom';

/**
 * Breadcrumbs (A5e)
 *
 * Home > Category > Subcategory > Product
 * Uses react-router-dom location for auto-generation.
 * Can also accept explicit items prop for custom trails.
 *
 * Source: Catalog Breadcrumb.jsx (chevron separator, home icon, truncation)
 * Mockup ref: general — appears on all interior pages
 */

// Route label mapping (static segments → display labels)
const ROUTE_LABELS = {
  products: 'Products',
  product: 'Product',
  'tool-finder': 'Tool finder',
  'drill-string-configurator': 'Drill string configurator',
  search: 'Search results',
  compare: 'Compare',
  favourites: 'Favourites',
  cart: 'Shopping cart',
  checkout: 'Checkout',
  orders: 'Orders',
  profile: 'My profile',
  cases: 'Support cases',
  documents: 'Documents',
  contact: 'Contact us',
  login: 'Sign in',
  admin: 'Administration',
};

/**
 * Generate breadcrumb items from current URL path
 */
function generateFromPath(pathname) {
  if (pathname === '/') return [];

  const segments = pathname.split('/').filter(Boolean);
  const items = [];

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;

    // Try static label, fallback to formatted segment
    const label = ROUTE_LABELS[segment] ||
      decodeURIComponent(segment)
        .replace(/-/g, ' ')
        .replace(/^\w/, c => c.toUpperCase());

    items.push({
      label,
      to: isLast ? null : currentPath,
      isLast,
    });
  });

  return items;
}

/**
 * Breadcrumbs component
 *
 * @param {Array} items - Optional explicit items: [{label, to}]. If omitted, auto-generates from URL.
 * @param {string} className - Additional CSS classes
 */
export default function Breadcrumbs({ items: explicitItems, className = '' }) {
  const location = useLocation();
  const items = explicitItems || generateFromPath(location.pathname);

  // Don't render on home page
  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-1.5 py-3 overflow-x-auto scrollbar-hide ${className}`}
    >
      {/* Home icon */}
      <Link
        to="/"
        className="text-n5 hover:text-carbon-black transition-colors flex-shrink-0"
        aria-label="Home"
      >
        <span className="material-symbols-outlined text-lg">home</span>
      </Link>

      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5 min-w-0">
          {/* Chevron separator */}
          <span className="material-symbols-outlined text-n4 text-sm flex-shrink-0">
            chevron_right
          </span>

          {/* Breadcrumb item */}
          {item.isLast || !item.to ? (
            <span className="text-sm text-n6 font-sandvik-text truncate max-w-[200px]">
              {item.label}
            </span>
          ) : (
            <Link
              to={item.to}
              className="text-sm text-carbon-black hover:text-electric-blue hover:underline transition-colors font-sandvik-text truncate max-w-[150px]"
            >
              {item.label}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
}
